import {EventSourcePolyfill} from 'event-source-polyfill';
import {isEqual} from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ProjectStatus} from '@contentlab/types/project';
import {ProjectStatusUpdate} from '@contentlab/types/projectStatusUpdate';
import Fortress from '@/services/fortress/Fortress';
import mapKeysToCase from '@/utils/mapKeysToCase';
import queryClient from '@/utils/reactQueryClient';
import {tokenSerializer} from '@/utils/token';
import {getEnv} from '@/config/environment';
import useBrandId from '@/hooks/use-brand-id';
import useDataQuery from '@/hooks/use-data-query';
import {CONTENT_LAB, PROJECTS_STATUS} from '../consts';

// Intermediate type for project before parsing progressInfo
type ProjectWithStringProgress = Omit<ProjectStatusUpdate, 'progressInfo'> & {
  progressInfo: string;
};

export const getQueryKey = (updatedHoursAgo: number) => [
  CONTENT_LAB,
  PROJECTS_STATUS,
  updatedHoursAgo,
];

export type ProjectUpdateReason = 'ADDED' | 'REMOVED' | 'UPDATE';

function getChangeReason(
  previousData: ProjectStatusUpdate[],
  newData: ProjectStatusUpdate[]
): ProjectUpdateReason {
  const previousIds = new Set(previousData.map((p) => p.projectId));
  const newIds = new Set(newData.map((p) => p.projectId));

  if (newIds.size > previousIds.size) {
    return 'ADDED';
  }
  if (newIds.size < previousIds.size) {
    return 'REMOVED';
  }
  return 'UPDATE';
}

function useProjectsStatusQuery({
  enabled = true,
  updatedHoursAgo = 24,
  onChange,
}: {
  enabled?: boolean;
  updatedHoursAgo?: number;
  onChange?: (reason: ProjectUpdateReason, data: ProjectStatusUpdate[]) => void;
}): {data: ProjectStatusUpdate[]; isLoading: boolean} {
  const [_, setLastUpdateData] = useState(null);
  const baseUrl = getEnv().VITE_CONTENT_STUDIO_API_HOST;
  const brandId = useBrandId();

  const {data, isLoading} = useDataQuery({
    queryKey: getQueryKey(updatedHoursAgo),
    // Temporary queryFn with 1s delay to prevent isLoading from changing immediately
    // This maintains loading state while SSE connection establishes and populates real data
    queryFn: async () =>
      new Promise((resolve) => {
        setTimeout(
          () =>
            resolve(
              queryClient.getQueryData(getQueryKey(updatedHoursAgo)) || []
            ),
          1000
        );
      }),
    enabled: !!brandId,
  });

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const parseProjectsFromSSE = useCallback((event: MessageEvent) => {
    const fixedJsonString = (event as MessageEvent).data
      .replace(/'/g, '"')
      .replace(/None/g, 'null');
    const parsedData = JSON.parse(fixedJsonString);
    const camelCaseData = mapKeysToCase(parsedData);
    const dataWithParsedProgressInfo = camelCaseData.map(
      (project: ProjectWithStringProgress) => {
        return {
          ...project,
          progressInfo: project.progressInfo,
        };
      }
    );

    const sortedProjects = dataWithParsedProgressInfo.sort(
      (a: ProjectStatusUpdate, b: ProjectStatusUpdate) => {
        if (
          a.status === ProjectStatus.IN_PROGRESS &&
          b.status === ProjectStatus.READY
        ) {
          return -1;
        }
        return 1;
      }
    );

    // use arrow function to avoid unnecessary re-renders when checking if there is a change
    setLastUpdateData((_lastUpdateData) => {
      if (!isEqual(_lastUpdateData, sortedProjects)) {
        if (_lastUpdateData) {
          const reason = getChangeReason(_lastUpdateData, sortedProjects);
          onChangeRef.current?.(reason, sortedProjects);
        }
        return sortedProjects;
      }
      return _lastUpdateData;
    });
    queryClient.setQueryData(getQueryKey(updatedHoursAgo), {
      data: sortedProjects,
    });
  }, []);

  useEffect(() => {
    if (!brandId || !enabled) {
      return () => {};
    }

    async function setupEventSource() {
      const authHeaders = await tokenSerializer();
      const eventSource = new EventSourcePolyfill(
        `${baseUrl}/api/v1/projects/status?updated_hours_ago=${updatedHoursAgo}`,
        {
          headers: {
            'x-lightricks-auth-token': (await Fortress.getAccessToken()) || '',
            'x-brand-id': brandId,
            ...(authHeaders || {}),
          },
        }
      );

      eventSource.addEventListener('open', () => {
        console.log('SSE connection opened');
      });

      eventSource.addEventListener('progress', (event) => {
        parseProjectsFromSSE(event as MessageEvent);
      });

      eventSource.addEventListener('completion', (event) => {
        parseProjectsFromSSE(event as MessageEvent);
      });

      return eventSource;
    }

    const eventSourcePromise = setupEventSource();

    return () => {
      eventSourcePromise.then((eventSource) => {
        console.log('Closing SSE connection');
        eventSource.close();
      });
    };
  }, [queryClient, brandId, enabled]);

  return useMemo(() => {
    return {data: (data || []) as ProjectStatusUpdate[], isLoading};
  }, [data, isLoading]);
}

export default useProjectsStatusQuery;
